import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Loader2, ArrowLeft, ArrowRight, Lightbulb, AlertTriangle, MessageCircle, Sparkles, GraduationCap, BookOpen } from 'lucide-react';
import MotoShell from '../components/MotoShell';
import { api } from '../lib/api';

const SECTION_STYLES = {
  didyouknow: { label: 'Le savais-tu ?', icon: Sparkles, cls: 'border-sky-500/30 bg-sky-500/[0.06] text-sky-300' },
  tip: { label: 'Conseil du moniteur', icon: Lightbulb, cls: 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300' },
  trap: { label: 'Le piège', icon: AlertTriangle, cls: 'border-amber-500/30 bg-amber-500/[0.06] text-amber-300' },
  story: { label: 'Témoignage', icon: MessageCircle, cls: 'border-rose-500/30 bg-rose-500/[0.06] text-rose-300' },
};

function Html({ html, className }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function MotoArticlePage({ kind }) {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setDoc(null); setErr(false);
    const fetcher = kind === 'dossier' ? api.motoDossier : api.motoEnquete;
    fetcher(slug).then((d) => {
      setDoc(d);
      document.title = `${d.title} | Code 2 Roues`;
      window.scrollTo(0, 0);
    }).catch(() => setErr(true));
  }, [kind, slug]);

  if (err) return <MotoShell><div className="max-w-3xl mx-auto px-4 py-24 text-center text-white/60">Article introuvable. <Link to="/moto" className="text-sky-400">← Retour</Link></div></MotoShell>;
  if (!doc) return <MotoShell><div className="py-32 flex justify-center"><Loader2 className="animate-spin text-sky-400" size={28} /></div></MotoShell>;

  const backTo = kind === 'dossier' ? '/moto/apprendre' : '/moto/enquetes';
  const backLabel = kind === 'dossier' ? 'Tous les dossiers ETM' : 'Toutes les enquêtes';

  return (
    <MotoShell>
      <article className="max-w-3xl mx-auto px-4 lg:px-6 pt-8 sm:pt-12" data-testid={`moto-article-${doc.slug}`}>
        <Link to={backTo} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-[12.5px] transition-colors" data-testid="moto-article-back">
          <ArrowLeft size={13} /> {backLabel}
        </Link>
        <div className="mt-5 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-[0.2em] font-bold">{doc.kicker}</div>
        <h1 className="font-serif-editorial text-[28px] sm:text-[42px] leading-[1.08] mt-2">{doc.title}</h1>
        <p className="text-white/60 text-[14px] sm:text-[15.5px] mt-3 leading-relaxed">{doc.subtitle}</p>
        <div className="mt-4 flex items-center gap-3 text-white/40 text-[11.5px] font-mono-spec">
          <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {doc.readTimeMin} min</span>
          {doc.issueNumber && <span>N°{doc.issueNumber}{doc.totalIssues ? `/${doc.totalIssues}` : ''}</span>}
          <span>{doc.category}</span>
        </div>
        <div className="mt-6 rounded-2xl overflow-hidden border border-white/10">
          <img src={doc.heroImage} alt={doc.heroImageAlt || doc.title} className="w-full h-56 sm:h-80 object-cover" />
        </div>

        {doc.edito && (
          <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-white/45 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-2">Édito</div>
            <div className="font-serif-editorial text-white text-[19px] leading-snug">{doc.edito.title}</div>
            <Html html={doc.edito.body} className="text-white/70 text-[13.5px] leading-relaxed mt-2 article-html" />
            {doc.edito.signedBy && <div className="text-sky-300 text-[12px] mt-3 italic">— {doc.edito.signedBy}</div>}
          </div>
        )}

        <div className="mt-8 space-y-8">
          {(doc.sections || []).map((s, i) => {
            if (s.type === 'lesson') {
              return (
                <section key={i}>
                  {s.partLabel && <div className="text-sky-400 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">{s.partLabel}</div>}
                  <h2 className="font-serif-editorial text-white text-[22px] sm:text-[28px] leading-tight mt-1.5">{s.title}</h2>
                  <div className="mt-3 space-y-3">
                    {(s.paragraphs || []).map((p, j) => <Html key={j} html={p} className="text-white/75 text-[14px] leading-[1.75] article-html" />)}
                  </div>
                  {(s.keyPoints || []).length > 0 && (
                    <ul className="mt-4 space-y-2 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      {s.keyPoints.map((k, j) => (
                        <li key={j} className="flex gap-2.5 text-[13px] text-white/80"><span className="text-sky-400 shrink-0">▸</span><Html html={k} className="article-html" /></li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            }
            const st = SECTION_STYLES[s.type];
            if (!st) return null;
            const Icon = st.icon;
            return (
              <aside key={i} className={`rounded-xl border p-5 ${st.cls.split(' ').slice(0, 2).join(' ')}`}>
                <div className={`text-[10.5px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5 ${st.cls.split(' ')[2]}`}>
                  <Icon size={12} /> {st.label}
                </div>
                <div className="font-serif-editorial text-white text-[18px] leading-snug mt-2">{s.title}</div>
                <Html html={s.body} className="text-white/75 text-[13.5px] leading-relaxed mt-2 article-html" />
              </aside>
            );
          })}
        </div>

        {doc.keyStat && (
          <div className="mt-10 rounded-2xl border border-sky-500/25 bg-gradient-to-br from-[#081020] to-[#0b0f1a] p-6 sm:p-8 text-center">
            <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">{doc.keyStat.kicker}</div>
            <div className="font-serif-editorial text-sky-300 text-[52px] sm:text-[72px] leading-none mt-2">{doc.keyStat.big}</div>
            {doc.keyStat.middle && <div className="text-white text-[16px] mt-1">{doc.keyStat.middle}</div>}
            <div className="text-white/55 text-[13px] mt-2 max-w-md mx-auto">{doc.keyStat.label}</div>
            {doc.keyStat.source && <div className="text-white/30 text-[11px] font-mono-spec mt-3">{doc.keyStat.source}</div>}
          </div>
        )}

        {(doc.glossary || []).length > 0 && (
          <div className="mt-10">
            <h2 className="font-serif-editorial text-white text-[22px]">Lexique</h2>
            <dl className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {doc.glossary.map((g, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5">
                  <dt className="text-sky-300 text-[13px] font-bold">{g.term}</dt>
                  <dd className="text-white/60 text-[12.5px] leading-snug mt-1">{g.def}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {doc.ecsrCorner && (
          <div className="mt-10 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-5">
            <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5"><GraduationCap size={12} /> Le coin ECSR</div>
            <div className="font-serif-editorial text-white text-[19px] mt-2">{doc.ecsrCorner.title}</div>
            {doc.ecsrCorner.intro && <Html html={doc.ecsrCorner.intro} className="text-white/70 text-[13.5px] leading-relaxed mt-2 article-html" />}
            {(doc.ecsrCorner.points || []).length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {doc.ecsrCorner.points.map((p, i) => <li key={i} className="flex gap-2 text-[13px] text-white/75"><span className="text-emerald-400 shrink-0">•</span><Html html={p} className="article-html" /></li>)}
              </ul>
            )}
          </div>
        )}

        {/* Satellites (dossiers uniquement) */}
        {kind === 'dossier' && (doc.satellites || []).length > 0 && (
          <div className="mt-12" data-testid="moto-satellites">
            <div className="text-sky-400 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">Pour aller plus loin</div>
            <h2 className="font-serif-editorial text-white text-[24px] mt-1">Les satellites du dossier</h2>
            <div className="mt-4 space-y-2.5">
              {doc.satellites.map((s) => {
                const published = s.status === 'published';
                const inner = (
                  <>
                    <BookOpen size={16} className={published ? 'text-sky-400 shrink-0' : 'text-white/25 shrink-0'} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[14px] font-semibold ${published ? 'text-white' : 'text-white/45'}`}>{s.title}</span>
                      <span className="block text-white/45 text-[12px] leading-snug mt-0.5 line-clamp-2">{s.teaser}</span>
                    </span>
                    {published
                      ? <ArrowRight size={14} className="text-sky-400 shrink-0" />
                      : <span className="shrink-0 text-[10px] font-mono-spec uppercase tracking-wider text-white/35 border border-white/10 rounded-full px-2 py-0.5">À paraître</span>}
                  </>
                );
                return published ? (
                  <Link key={s.slug} to={`/moto/apprendre/${doc.slug}/${s.slug}`} data-testid={`moto-sat-${s.slug}`}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-sky-500/40 p-4 transition-colors">{inner}</Link>
                ) : (
                  <div key={s.slug} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.01] p-4">{inner}</div>
                );
              })}
            </div>
          </div>
        )}

        {doc.nextIssue && (
          <Link to={`/moto/${kind === 'dossier' ? 'apprendre' : 'enquete'}/${doc.nextIssue.slug}`} data-testid="moto-next-issue"
            className="mt-12 mb-4 flex items-center justify-between gap-4 rounded-2xl border border-sky-500/30 bg-sky-500/[0.06] hover:bg-sky-500/[0.12] p-5 transition-colors">
            <div>
              <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold">Numéro suivant{doc.nextIssue.number ? ` · N°${doc.nextIssue.number}` : ''}</div>
              <div className="font-serif-editorial text-white text-[19px] mt-1">{doc.nextIssue.title}</div>
            </div>
            <ArrowRight size={18} className="text-sky-400 shrink-0" />
          </Link>
        )}
      </article>
    </MotoShell>
  );
}
